'use strict';

const { BaseWidget } = require('./BaseWidget');
const { h } = require('./dom');
const { BX, bankAccent } = require('./theme');
const { formatCurrency, maskAccount, formatDateTime } = require('./format');
const rd = require('./rd');

function pick(obj, ...keys) {
  for (const k of keys) {
    if (obj && obj[k] != null && obj[k] !== '') return obj[k];
  }
  return undefined;
}

function genRefId() {
  return `RF${Date.now()}${Math.floor(Math.random() * 1000)}`;
}

/**
 * Best-effort NSDL response -> UI status mapping, ported from webpayment's
 * stores/dmt.ts#mapTxnStatus. Unknown codes default to PENDING rather than
 * FAILED, so the requery poller gets a chance to resolve them.
 */
function mapTxnStatus(res) {
  const raw = String(pick(res?.data, 'status', 'txnStatus') ?? res?.status ?? '').toUpperCase();
  if (['SUCCESS', 'SUCCESSFUL', 'COMPLETED'].includes(raw)) return 'SUCCESS';
  if (['FAILED', 'FAILURE', 'DECLINED', 'REJECTED', 'REVERSED'].includes(raw)) return 'FAILED';
  if (['PENDING', 'PROCESSING', 'INITIATED', 'IN PROGRESS'].includes(raw)) return 'PENDING';
  if (res?.statusCode === '00') return 'SUCCESS';
  if (res?.statusCode === '01') return 'FAILED';
  return 'PENDING';
}

/** Normalizes remitterDetails' beneficiarydetail — NSDL's XML->JSON conversion
 *  collapses a single repeated element to a bare object instead of a one-item
 *  array, so a remitter with exactly one beneficiary would otherwise be dropped. */
function normalizeBeneficiaryList(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object' && (raw.beneficiaryid != null || raw.accountnumber != null)) return [raw];
  return [];
}

async function captureBiometric() {
  const device = await rd.findFirstConnectedDevice();
  if (!device) return { ok: false, message: 'No RD biometric service found running on this machine' };
  return rd.captureFingerprint(device.vendor);
}

/**
 * DmtWidget — Domestic Money Transfer. Mirrors webpayment's DMT flow
 * (stores/dmt.ts + pages/merchant/dmt/*) as closely as a headless widget
 * can: remitter lookup/KYC onboarding, existing-beneficiary list (from the
 * remitter details response) or add-new, OTP, instant transfer with
 * PENDING/requery polling.
 *
 * Deliberately does NOT preview the transfer charge/GST before sending the
 * OTP — the customer never sees a charge breakdown pre-transaction. The
 * backend (dmt.txn.controller.js#instantTransaction) fetches NSDL's live
 * charge itself when it actually processes the transfer, independent of
 * anything this widget does, so there's no charges API call in this flow
 * at all — not even a silent background one.
 *
 * Every retryable screen takes an optional leading `errorMsg` — screens are
 * torn down and rebuilt on retry (see BaseWidget#showScreen), so an error
 * banner must be built into the *new* screen's own error slot rather than
 * written into a slot captured by the previous render's closure, which would
 * already be detached from the DOM by the time the banner is added.
 *
 * const widget = new DmtWidget(client, { onSuccess, onError });
 * widget.mount('#bbx-dmt').start();
 */
class DmtWidget extends BaseWidget {
  start() {
    this._p = { beneficiaries: [] };
    this.screenMobile();
    return this;
  }

  unmount() {
    this._pollCancelled = true;
    super.unmount();
  }

  screenMobile(errorMsg) {
    this.setHeader({ title: 'Money Transfer', subtitle: "Enter the sender's mobile number", showBack: false });

    const mobileField = this.field({ label: 'Remitter Mobile Number', inputmode: 'numeric', maxlength: '10', placeholder: '10-digit mobile number', value: this._p.mobile || '', onInput: (v) => { this._p.mobile = v; } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        mobileField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn',
          text: 'Continue',
          onClick: async (e) => {
            if (!this._p.mobile || this._p.mobile.length !== 10) {
              errorSlot.replaceChildren(this.banner('error', 'Enter a valid 10-digit mobile number'));
              return;
            }
            e.target.disabled = true;
            this.renderLoading('Looking up remitter…');
            try {
              const detail = await this.client.dmt.getCustomerDetail({ mobile_number: this._p.mobile });

              if (detail.statusCode === '00' && detail.data) {
                this._p.referenceid = pick(detail.data, 'referenceid', 'referenceId');
                await this.resolveRemitter();
                return;
              }

              // Distinguish "customer genuinely doesn't exist yet" (proceed to
              // onboarding) from a real error — bad credentials, merchant not
              // found/authorized, network failure, etc. (halt and show it).
              // Collapsing these into one "anything but success -> onboard"
              // branch would silently walk past errors like paymentSystem's
              // "MerchantId required or not Found" straight into a transaction
              // flow. Ported from webpayment's stores/dmt.ts#searchCustomer.
              const notFoundMessage = /no\s*customer\s*found|customer\s*not\s*found/i.test(detail.message || detail.data?.response || '');
              if (detail.statusCode === '01' || detail.statusCode === '404' || (detail.statusCode === '99' && notFoundMessage)) {
                this.screenOnboard();
                return;
              }

              this.fail(detail, detail.message);
              this.screenMobile(detail.message || 'Could not verify customer right now');
            } catch (err) {
              this.fail(err);
              this.screenMobile(this.errorMessage(err, 'Network error — please try again'));
            }
          },
        }),
      ]),
    );
  }

  /** Existing (or freshly onboarded) customer — load/register their NSDL remitter
   *  and the beneficiary list embedded in that response. */
  async resolveRemitter() {
    this.renderLoading('Preparing remitter…');
    try {
      let res = await this.client.dmt.getRemitterDetails({ sender_mobilenumber: this._p.mobile });

      if (res.statusCode === '400') {
        const regRes = await this.client.dmt.registerRemitter({ sender_mobilenumber: this._p.mobile });
        if (regRes.statusCode !== '00') {
          this.fail(regRes, regRes.message);
          this.screenMobile(regRes.message || 'Could not register remitter');
          return;
        }
        res = await this.client.dmt.getRemitterDetails({ sender_mobilenumber: this._p.mobile });
      }

      if (res.statusCode !== '00' || !res.data) {
        this.fail(res, res.message);
        this.screenMobile(res.message || 'Remitter details are unavailable');
        return;
      }

      // Confirmed live response shape (paymentSystem /dmt/txn/v1/remitter/details):
      // { senderId, AviableLimit, AviabledayLimit, beneficiarydetail }
      const d = res.data;
      const dailyLimit = Number(pick(d, 'AviabledayLimit', 'dailyLimit') ?? 25000);
      const remainingLimit = Number(pick(d, 'AviableLimit', 'remainingLimit') ?? dailyLimit);

      this._p.senderId = d.senderId ?? this._p.mobile;
      this._p.remainingLimit = remainingLimit;

      const beneficiaryList = normalizeBeneficiaryList(d.beneficiarydetail);
      this._p.beneficiaries = beneficiaryList.map((b) => ({
        id: String(b.beneficiaryid),
        name: b.beneficiaryname ?? '',
        mobile: b.beneficiarymobilenumber ? String(b.beneficiarymobilenumber) : '',
        accountNumber: String(b.accountnumber ?? ''),
        ifsc: b.ifscode ?? '',
        bankName: b.bank ?? '',
        verified: Number(b.beneficiarystatus) === 1,
      }));

      this.screenBeneficiaryList();
    } catch (err) {
      this.fail(err);
      this.screenMobile(this.errorMessage(err, 'Unable to prepare remitter'));
    }
  }

  // ── New remitter KYC onboarding ───────────────────────────────────

  screenOnboard(errorMsg) {
    this.setHeader({ title: 'New Remitter', subtitle: 'A few details to register the sender', showBack: true, onBack: () => this.screenMobile() });

    const nameField = this.field({ label: 'Full Name', value: this._p.name || '', onInput: (v) => { this._p.name = v; } });
    const emailField = this.field({ label: 'Email', type: 'email', value: this._p.email || '', onInput: (v) => { this._p.email = v; } });
    const cityField = this.field({ label: 'City', value: this._p.city || '', onInput: (v) => { this._p.city = v; } });
    const stateField = this.field({ label: 'State', value: this._p.state || '', onInput: (v) => { this._p.state = v; } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        nameField.node, emailField.node, cityField.node, stateField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn',
          text: 'Register',
          onClick: async (e) => {
            if (!this._p.name || !this._p.city || !this._p.state) {
              errorSlot.replaceChildren(this.banner('error', 'Fill in name, city and state'));
              return;
            }
            e.target.disabled = true;
            this.renderLoading('Registering…');
            try {
              const res = await this.client.dmt.onboardCustomer({ name: this._p.name, mobile_no: this._p.mobile, email_id: this._p.email, city: this._p.city, state: this._p.state });
              if (res.statusCode !== '00') {
                this.fail(res, res.message);
                this.screenOnboard(res.message || 'Registration failed');
                return;
              }

              // Onboarding's real response (confirmed live in webpayment) puts
              // requestId/otp_status/otp_reqid at the TOP level, not under data —
              // requestId is what every later step expects as `referenceid`.
              const referenceid = res.requestId != null ? String(res.requestId) : pick(res.data, 'referenceid', 'referenceId');
              if (!referenceid) {
                this.screenOnboard('Registration succeeded but no reference id was returned');
                return;
              }
              this._p.referenceid = referenceid;
              this._p.otpReqId = pick(res, 'otp_reqid') ?? pick(res.data, 'otp_reqid', 'otpReqId');

              // onboarding() auto-fires request_otp server-side — only resend
              // explicitly if that auto-send failed.
              if (res.otp_status === false) {
                const otpRes = await this.client.dmt.requestCustomerOtp({ referenceid });
                if (otpRes.statusCode !== '00') {
                  this.fail(otpRes, otpRes.message);
                  this.screenOnboard(otpRes.message || 'Registered, but could not send OTP');
                  return;
                }
                this._p.otpReqId = pick(otpRes.data, 'otpreqid', 'otpReqId') ?? this._p.otpReqId;
              }

              this.screenOnboardOtp();
            } catch (err) {
              this.fail(err);
              this.screenOnboard(this.errorMessage(err, 'Registration failed'));
            }
          },
        }),
      ]),
    );
  }

  screenOnboardOtp(errorMsg) {
    this.setHeader({ title: 'Verify OTP', subtitle: `Sent to ${this._p.mobile}`, showBack: true, onBack: () => this.screenOnboard() });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    const otp = this.buildOtpInput({
      onComplete: async (value) => {
        try {
          const res = await this.client.dmt.validateCustomerOtp({ otp: value, otpreqid: this._p.otpReqId ?? '', referenceid: this._p.referenceid });
          if (res.statusCode !== '00') {
            otp.reset();
            errorSlot.replaceChildren(this.banner('error', res.message || 'Incorrect OTP'));
            return;
          }
          this.screenOnboardAadhaar();
        } catch (err) {
          otp.reset();
          errorSlot.replaceChildren(this.banner('error', this.errorMessage(err, 'Incorrect OTP')));
        }
      },
      onResend: () => this.client.dmt.requestCustomerOtp({ referenceid: this._p.referenceid }).then((r) => { this._p.otpReqId = pick(r.data, 'otpreqid', 'otpReqId') ?? this._p.otpReqId; }),
    });

    this.showScreen(h('div', {}, [otp.node, errorSlot]));
  }

  screenOnboardAadhaar(errorMsg) {
    this.setHeader({ title: 'Aadhaar Verification', showBack: false });
    const aadhaarField = this.field({ label: 'Aadhaar Number', inputmode: 'numeric', maxlength: '12', placeholder: '12-digit Aadhaar', value: this._p.aadhaar || '', onInput: (v) => { this._p.aadhaar = v; } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);
    const statusSlot = h('p', { class: 'bbx-muted' });

    this.showScreen(
      h('div', {}, [
        aadhaarField.node,
        errorSlot,
        statusSlot,
        h('button', {
          class: 'bbx-btn',
          text: 'Scan Fingerprint & Verify',
          onClick: async (e) => {
            if (!this._p.aadhaar || this._p.aadhaar.length !== 12) {
              errorSlot.replaceChildren(this.banner('error', 'Enter a valid 12-digit Aadhaar number'));
              return;
            }
            e.target.disabled = true;
            errorSlot.replaceChildren();
            try {
              const uidRes = await this.client.dmt.verifyCustomerUid({ aadhaarno: this._p.aadhaar, referenceid: this._p.referenceid });
              if (uidRes.statusCode !== '00') {
                this.fail(uidRes, uidRes.message);
                this.screenOnboardAadhaar(uidRes.message || 'Aadhaar verification failed');
                return;
              }

              statusSlot.textContent = 'Place finger on the scanner…';
              const capture = await captureBiometric();
              if (!capture.ok) {
                this.screenOnboardAadhaar(capture.message);
                return;
              }

              statusSlot.textContent = 'Submitting…';
              // updatebiodetail already chains NSDL confirmation + statusCheck
              // server-side on success — a separate submitCustomer() call here
              // would re-confirm the same referenceid a second time and risk
              // NSDL rejecting the duplicate (see webpayment's stores/dmt.ts).
              const bioRes = await this.client.dmt.updateCustomerBiodetail({ PidData: capture.pidDataXml, aadhaarno: this._p.aadhaar, referenceid: this._p.referenceid });
              if (bioRes.statusCode !== '00') {
                this.fail(bioRes, bioRes.message);
                this.screenOnboardAadhaar(bioRes.message || 'Biometric verification failed');
                return;
              }
              await this.resolveRemitter();
            } catch (err) {
              this.fail(err);
              this.screenOnboardAadhaar(this.errorMessage(err, 'Verification failed'));
            }
          },
        }),
      ]),
    );
  }

  // ── Beneficiary list ───────────────────────────────────────────────

  screenBeneficiaryList() {
    this.setHeader({ title: 'Beneficiaries', subtitle: `Remaining daily limit: ${formatCurrency(this._p.remainingLimit)}`, showBack: false });

    const list = this._p.beneficiaries.length
      ? this._p.beneficiaries.map((ben) =>
          h('div', { class: 'bbx-ben-card', onClick: () => { if (ben.verified) this.selectBeneficiary(ben); } }, [
            h('div', { class: 'bbx-ben-logo', style: { background: bankAccent(ben.ifsc) }, text: (ben.name || '??').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase() || 'BK' }),
            h('div', { style: { flex: '1', minWidth: '0' } }, [
              h('div', { class: 'bbx-ben-name', text: ben.name || ben.accountNumber }),
              h('div', { class: 'bbx-ben-sub bbx-mono', text: `${maskAccount(ben.accountNumber)} · ${ben.ifsc}` }),
              !ben.verified ? h('div', { class: 'bbx-hint', style: { color: BX.warning }, text: 'Not verified' }) : null,
            ]),
          ]),
        )
      : [h('div', { class: 'bbx-empty', text: 'No beneficiaries yet — add one to send money.' })];

    this.showScreen(
      h('div', {}, [
        ...list,
        h('button', { class: 'bbx-btn bbx-btn-secondary', style: { marginTop: '10px' }, text: '+ Add New Beneficiary', onClick: () => this.screenBeneficiaryForm() }),
      ]),
    );
  }

  selectBeneficiary(ben) {
    this._p.selectedBeneficiary = ben;
    this.screenTransfer();
  }

  screenBeneficiaryForm(errorMsg) {
    this.setHeader({ title: 'Add Beneficiary', subtitle: 'Who are you sending money to?', showBack: true, onBack: () => this.screenBeneficiaryList() });

    const nameField = this.field({ label: 'Account Holder Name', value: this._p.benName || '', onInput: (v) => { this._p.benName = v; } });
    const accField = this.field({ label: 'Account Number', inputmode: 'numeric', value: this._p.accountNumber || '', onInput: (v) => { this._p.accountNumber = v; } });
    const ifscField = this.field({ label: 'IFSC Code', value: this._p.ifsc || '', onInput: (v) => { this._p.ifsc = (v || '').toUpperCase(); } });
    const mobileField = this.field({ label: 'Beneficiary Mobile (optional)', inputmode: 'numeric', value: this._p.benMobile || '', onInput: (v) => { this._p.benMobile = v; } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        nameField.node, accField.node, ifscField.node, mobileField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn',
          text: 'Verify & Add Beneficiary',
          onClick: async (e) => {
            if (!this._p.benName || !this._p.accountNumber || !this._p.ifsc) {
              errorSlot.replaceChildren(this.banner('error', 'Fill in name, account number and IFSC'));
              return;
            }
            e.target.disabled = true;
            this.renderLoading('Verifying account…');
            try {
              // Field names match dmt.txn.controller.js#accountVerification exactly
              // (receiver_account_no/receiverIfscCode) — an earlier version of this
              // call sent accountnumber/ifsccode, which the backend never read.
              const verify = await this.client.dmt.accountVerification({
                sender_mobilenumber: this._p.mobile,
                receiver_account_no: this._p.accountNumber,
                receiverIfscCode: this._p.ifsc,
                receivername: this._p.benName,
                receivermobilenumber: this._p.benMobile,
              });
              if (verify.statusCode !== '00') {
                this.fail(verify, verify.message);
                this.screenBeneficiaryForm(verify.message || 'Account verification failed');
                return;
              }
              const holderName = pick(verify.data, 'account_holder_name', 'accountHolderName') ?? this._p.benName;

              const added = await this.client.dmt.addBeneficiary({
                senderId: this._p.senderId,
                receivername: this._p.benName,
                receivermobilenumber: this._p.benMobile,
                receiverIfscCode: this._p.ifsc,
                receiver_account_no: this._p.accountNumber,
              });
              if (added.statusCode !== '00') {
                this.fail(added, added.message);
                this.screenBeneficiaryForm(added.message || 'Unable to add beneficiary');
                return;
              }

              // paymentSystem nests the new id under data.beneficiarydetail.beneficiaryId —
              // reading data.beneficiaryId directly always misses.
              const bd = added.data?.beneficiarydetail ?? added.data ?? {};
              const ben = {
                id: String(pick(bd, 'beneficiaryId', 'BeneficiaryId') ?? this._p.accountNumber),
                name: holderName,
                mobile: this._p.benMobile || '',
                accountNumber: this._p.accountNumber,
                ifsc: this._p.ifsc,
                bankName: '',
                verified: true,
              };
              this._p.beneficiaries.push(ben);
              this.selectBeneficiary(ben);
            } catch (err) {
              this.fail(err);
              this.screenBeneficiaryForm(this.errorMessage(err, 'Unable to add beneficiary'));
            }
          },
        }),
      ]),
    );
  }

  // ── Transfer ─────────────────────────────────────────────────────

  screenTransfer(errorMsg) {
    const ben = this._p.selectedBeneficiary;
    this.setHeader({ title: 'Transfer Amount', subtitle: ben.name, showBack: true, onBack: () => this.screenBeneficiaryList() });

    const amountField = this.field({ label: 'Amount', type: 'number', placeholder: '0.00', value: this._p.amount ? String(this._p.amount) : '', onInput: (v) => { this._p.amount = v; } });
    const remarksField = this.field({ label: 'Remarks (optional)', value: this._p.remarks || '', onInput: (v) => { this._p.remarks = v; } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    // No charges preview here by design — the SDK never shows the transfer
    // charge/GST breakdown before processing. The backend independently
    // re-fetches NSDL's live charge when it actually processes the
    // transfer (dmt.txn.controller.js#instantTransaction step 3b), so
    // skipping transactionCharges() here has no bearing on what's actually
    // charged; it only removes a pre-transaction charge display.
    const actionBtn = h('button', {
      class: 'bbx-btn',
      text: 'Send OTP',
      onClick: async (e) => {
        const amt = Number(this._p.amount);
        if (!amt || amt <= 0) {
          errorSlot.replaceChildren(this.banner('error', 'Enter a valid amount'));
          return;
        }
        if (amt > this._p.remainingLimit) {
          errorSlot.replaceChildren(this.banner('error', `Amount exceeds remaining daily limit of ${formatCurrency(this._p.remainingLimit)}`));
          return;
        }
        this._p.amount = amt;
        errorSlot.replaceChildren();

        e.target.disabled = true;
        this.renderLoading('Sending OTP…');
        try {
          const res = await this.client.dmt.transactionOtp({ senderId: this._p.senderId, amount: amt, receiver_account_no: ben.accountNumber, receiverIfscCode: ben.ifsc });
          if (res.statusCode !== '00') {
            this.fail(res, res.message);
            this.screenTransfer(res.message || 'Unable to send OTP');
            return;
          }
          this._p.transferRefId = pick(res.data, 'refId', 'referenceId', 'refid');
          this.screenTransferOtp();
        } catch (err) {
          this.fail(err);
          this.screenTransfer(this.errorMessage(err, 'Unable to send OTP'));
        }
      },
    });

    this.showScreen(
      h('div', {}, [
        h('div', { class: 'bbx-card' }, [
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'To' }), h('span', { class: 'bbx-row-v', text: ben.name })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Account' }), h('span', { class: 'bbx-row-v bbx-mono', text: maskAccount(ben.accountNumber) })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'IFSC' }), h('span', { class: 'bbx-row-v bbx-mono', text: ben.ifsc })]),
        ]),
        amountField.node,
        remarksField.node,
        errorSlot,
        actionBtn,
      ]),
    );
  }

  screenTransferOtp(errorMsg) {
    this.setHeader({ title: 'Verify OTP', subtitle: `Sent to ${this._p.mobile}`, showBack: true, onBack: () => this.screenTransfer() });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    const otp = this.buildOtpInput({
      onComplete: (value) => this.submitTransfer(value, otp, errorSlot),
      onResend: () => this.client.dmt.transactionOtp({ senderId: this._p.senderId, amount: this._p.amount, receiver_account_no: this._p.selectedBeneficiary.accountNumber, receiverIfscCode: this._p.selectedBeneficiary.ifsc }),
    });

    this.showScreen(h('div', {}, [otp.node, errorSlot]));
  }

  async submitTransfer(otpValue, otp, errorSlot) {
    otp.stop();
    this.showOverlay('Processing your transfer…');
    this._p.refId = this._p.refId || genRefId();
    const ben = this._p.selectedBeneficiary;

    try {
      const res = await this.client.dmt.transactionInstant({
        senderId: this._p.senderId,
        BeneficiaryId: ben.id,
        amount: this._p.amount,
        refId: this._p.transferRefId || this._p.refId,
        remarks: this._p.remarks,
        otp: otpValue,
        receiver_account_no: ben.accountNumber,
        receiverIfscCode: ben.ifsc,
        account_holder_name: ben.name,
        receiverMobilenumber: ben.mobile,
      });
      this.hideOverlay();

      // A pre-flight validation/server error (bad field, insufficient balance,
      // wallet inactive, etc.) is rejected before NSDL is ever called and
      // carries no `data` — treat it as a hard failure, not an ambiguous
      // PENDING the requery poller could never resolve.
      if (res.statusCode !== '00' && !res.data) {
        this.screenResult(res, 'FAILED');
        return;
      }

      this.screenResult(res, mapTxnStatus(res));
    } catch (err) {
      this.hideOverlay();
      otp.reset();
      errorSlot.replaceChildren(this.banner('error', this.errorMessage(err, 'Transfer failed')));
      this.fail(err);
    }
  }

  screenResult(res, status) {
    const ben = this._p.selectedBeneficiary;
    const data = res.data || {};
    this._p.txn = {
      transactionId: pick(data, 'transactionId', 'txnId') ?? this._p.transferRefId ?? '',
      rrn: pick(data, 'rrn', 'RRN') ?? '',
      bankReference: pick(data, 'bankReference', 'bank_reference') ?? '',
      status,
      reason: status === 'FAILED' ? (res.message || 'Transaction was declined by the bank') : null,
    };

    this.setHeader({ title: 'Transfer', showBack: false });
    const meta = status === 'SUCCESS' ? { icon: '✓', bg: BX.success2 } : status === 'PENDING' ? { icon: '⏳', bg: BX.warning } : { icon: '✕', bg: BX.error };
    const title = status === 'SUCCESS' ? 'Transfer Successful' : status === 'PENDING' ? 'Transfer Pending' : 'Transfer Failed';

    const pollingSlot = h('p', { class: 'bbx-muted', style: { textAlign: 'center' } });
    const payload = { ...res, status, refId: this._p.transferRefId, amount: this._p.amount, beneficiary: ben.name };

    // succeed()/fail() fire when the user acknowledges the result (clicks
    // Done), not the instant it's known — firing immediately here meant a
    // caller whose onSuccess/onError closes the widget (the documented
    // Checkout/bucksboxHandler pattern does exactly that) would tear it down
    // before the customer ever saw this screen. If Done is clicked while
    // still PENDING (poll hasn't resolved yet), that's a dismissed,
    // unconfirmed transfer — reported as failure, same as the poller's own
    // exhausted-attempts fallback. `_resultReported` guards against firing
    // twice when both a still-visible Done button and the poller's own
    // timeout race for the same PENDING screen.
    this._resultReported = false;
    const reportResult = () => {
      if (this._resultReported) return;
      this._resultReported = true;
      if (status === 'SUCCESS') this.succeed(payload);
      else this.fail(res, this._p.txn.reason || 'No confirmation received — verify with the reference number before treating this as successful');
    };

    this.showScreen(
      h('div', {}, [
        h('div', { class: 'bbx-status-icon', style: { background: meta.bg }, text: meta.icon }),
        h('p', { class: 'bbx-status-title', text: title }),
        this._p.txn.reason ? h('p', { class: 'bbx-status-sub', text: this._p.txn.reason }) : null,
        status === 'PENDING' ? h('p', { class: 'bbx-status-sub', text: 'No confirmation received yet — checking automatically.' }) : null,
        h('div', { class: 'bbx-card' }, [
          h('div', { class: 'bbx-amount-hero', text: formatCurrency(this._p.amount) }),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Beneficiary' }), h('span', { class: 'bbx-row-v', text: ben.name })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Account' }), h('span', { class: 'bbx-row-v bbx-mono', text: maskAccount(ben.accountNumber) })]),
          this._p.txn.rrn ? h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'RRN' }), h('span', { class: 'bbx-row-v bbx-mono', text: this._p.txn.rrn })]) : null,
          this._p.txn.bankReference ? h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Bank Reference' }), h('span', { class: 'bbx-row-v bbx-mono', text: this._p.txn.bankReference })]) : null,
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Date & Time' }), h('span', { class: 'bbx-row-v', text: formatDateTime(Date.now()) })]),
        ]),
        pollingSlot,
        h('button', { class: 'bbx-btn', text: 'Done', onClick: () => { reportResult(); this.opts.onClose && this.opts.onClose(); } }),
      ]),
    );

    if (status === 'PENDING') this.pollTransactionStatus(pollingSlot);
  }

  /** Polls transaction/requery every 5s, up to 12 attempts — ported from
   *  webpayment's stores/dmt.ts#pollTransactionStatus. */
  async pollTransactionStatus(pollingSlot) {
    this._pollCancelled = false;
    const maxAttempts = 12;
    for (let attempt = 1; attempt <= maxAttempts && !this._pollCancelled; attempt++) {
      pollingSlot.textContent = `Checking status… (${attempt}/${maxAttempts})`;
      await new Promise((r) => setTimeout(r, 5000));
      if (this._pollCancelled) return;

      try {
        const res = await this.client.dmt.transactionRequery({
          refId: this._p.transferRefId || this._p.refId,
          transactionId: this._p.txn.transactionId,
          rrn: this._p.txn.rrn,
        });
        const status = mapTxnStatus(res);
        if (status !== 'PENDING') {
          pollingSlot.textContent = '';
          this.screenResult(res, status);
          return;
        }
      } catch (err) {
        // transient — keep polling until attempts are exhausted
      }
    }
    // Exhausted every attempt without a resolution. This is a one-shot
    // checkout the caller is waiting on a definitive outcome from — it must
    // always resolve to success or failure, never silently neither, even
    // though the on-screen card stays up (with the reference details) for
    // the merchant to verify manually. An unconfirmed transfer is treated
    // as failure here (can't confirm the debit/credit completed), not
    // success. Guarded by `_resultReported` — the Done button on the still-
    // visible PENDING screen calls the same instance-level guard, so
    // whichever fires first (user dismissing vs. the poller giving up) wins
    // and the other is a no-op, never a duplicate report.
    if (!this._pollCancelled && !this._resultReported) {
      this._resultReported = true;
      pollingSlot.textContent = 'Still pending — check back later or contact support with the reference above.';
      this.fail(
        { statusCode: '01', message: 'Transfer status could not be confirmed after polling' },
        'Transfer status could not be confirmed — verify with the reference number before treating this as successful',
      );
    }
  }
}

module.exports = { DmtWidget };
